import React from 'react';
import { useTour } from '@reactour/tour';
import {
	HouseLineIcon as HouseLine,
	UsersThreeIcon as UsersThree,
	GraduationCapIcon as GraduationCap,
	ImageSquareIcon as ImageSquare,
	ShieldWarningIcon as ShieldWarning,
	EnvelopeSimpleIcon as EnvelopeSimple,
	BookOpenTextIcon as BookOpenText,
	TargetIcon as Target,
	UserCircleIcon as UserCircle,
	StudentIcon as Student,
	EnvelopeSimpleOpenIcon as EnvelopeSimpleOpen,
	GaugeIcon as GaugeSimple,
	ChartLineUpIcon as ChartLineUp,
	BroadcastIcon as Broadcast,
} from '@phosphor-icons/react';

// Revamped product tour popover.
//
// Design goals (matches the UEMP design system):
//  - Fully theme-aware: every colour is a CSS token from _theme.scss
//    (--app-card-bg / --app-text* / --app-border / --app-accent*), so the tour
//    flips automatically between light and dark with the rest of the app.
//  - On-brand: the UEMP red accent replaces the old indigo/violet gradient.
//  - Phosphor icons (consistent with the sidebar) instead of emoji.
//  - Clean, readable layout with progress + step counter.
//
// @reactour calls each step's content() with { currentStep }.
const RichStep = ({ Icon, title, body, currentStep, totalSteps, chapters = [] }) => {
	const progress = Math.round(((currentStep + 1) / totalSteps) * 100);
	const { setCurrentStep } = useTour();
	// Guide §16: group into chapters with a navigator once a tour exceeds 12 steps.
	const showChapters = chapters.length > 1 && totalSteps > 12;
	const activeChapter = chapters.findIndex((c) => currentStep >= c.start && currentStep <= c.end);
	const pad = (n) => String(n).padStart(2, '0');
	return (
		<div style={{ fontFamily: 'inherit', color: 'var(--app-text)' }}>
			{/* Chapter navigator (only past 12 steps) */}
			{showChapters && (
				<div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
					{chapters.map((c, i) => {
						const active = i === activeChapter;
						return (
							<button
								key={c.name}
								type="button"
								onClick={() => setCurrentStep(c.start)}
								style={{
									cursor: 'pointer',
									border: `1px solid ${active ? 'var(--app-accent)' : 'var(--app-border)'}`,
									background: active ? 'var(--app-accent-soft)' : 'transparent',
									color: active ? 'var(--app-accent)' : 'var(--app-text-muted)',
									borderRadius: 999,
									padding: '3px 10px',
									fontSize: 11,
									fontWeight: 600,
								}}>
								{c.name}
							</button>
						);
					})}
				</div>
			)}

			{/* Step counter — zero-padded mono (guide §16) */}
			<div
				style={{
					fontFamily: 'var(--font-mono, ui-monospace, monospace)',
					fontSize: 11,
					fontWeight: 600,
					color: 'var(--app-text-muted)',
					letterSpacing: '0.08em',
					fontVariantNumeric: 'tabular-nums',
					marginBottom: 10,
				}}>
				{pad(currentStep + 1)}&nbsp;/&nbsp;{pad(totalSteps)}
			</div>

			{/* Progress rail — 3px (guide §16) */}
			<div
				style={{
					height: 3,
					background: 'var(--app-accent-soft)',
					borderRadius: 999,
					marginBottom: 16,
					overflow: 'hidden',
				}}>
				<div
					style={{
						height: '100%',
						width: `${progress}%`,
						background: 'var(--app-accent-fill)',
						borderRadius: 999,
						transition: 'width 0.35s ease',
					}}
				/>
			</div>

			{/* Header: icon chip + title */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 12,
					marginBottom: 12,
					paddingBottom: 12,
					borderBottom: '1px solid var(--app-border)',
				}}>
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 38,
						height: 38,
						flexShrink: 0,
						borderRadius: 10,
						background: 'var(--app-accent-soft)',
						color: 'var(--app-accent)',
					}}>
					<Icon size={20} weight="duotone" />
				</span>
				<p
					style={{
						fontSize: 15,
						fontWeight: 700,
						color: 'var(--app-text-heading)',
						margin: 0,
						lineHeight: 1.25,
					}}>
					{title}
				</p>
			</div>

			{/* Body */}
			<p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--app-text)', margin: 0 }}>{body}</p>
		</div>
	);
};

const STEP_DATA = [
	{
		selector: '.dashboard',
		Icon: HouseLine,
		title: 'Dashboard',
		body: 'Your command centre. Monitor active campaigns, user engagement, training progress, and your organisation’s overall risk score — all in real time, at a glance.',
	},
	{
		selector: '.users',
		Icon: UsersThree,
		title: 'Users & Groups',
		body: 'Manage the people in your programme. Import users from Excel, organise them into departments or groups, and track each person’s awareness progress. This defines who receives simulations and training.',
	},
	{
		selector: '.trainingModules',
		Icon: GraduationCap,
		title: 'Training Modules',
		body: 'Assign SCORM-compliant awareness courses covering phishing, passwords, and social engineering. Track completion and build lasting security habits across your teams.',
	},
	{
		selector: '.posters',
		Icon: ImageSquare,
		title: 'Awareness Posters',
		body: 'Download ready-made security-awareness posters to share across your workplace. Reinforce training and keep good security habits visible and top-of-mind.',
	},
	{
		selector: '.reports',
		Icon: ShieldWarning,
		title: 'Phishing Reporter',
		body: 'Review emails your people flagged as suspicious. Validate real-world reports, recognise vigilant users, and spot emerging threats aimed at your organisation.',
	},
	{
		selector: '.systemNotifiers',
		Icon: EnvelopeSimple,
		title: 'System Notifiers',
		body: 'Customise the automated emails the platform sends — campaign results, training assignments, onboarding, and password resets — so every message stays on-brand and consistent.',
	},
	{
		selector: '.instructionManual',
		Icon: BookOpenText,
		title: 'Instruction Manual',
		body: 'Your step-by-step guide to the platform: creating campaigns, managing users, reading reports, and getting the most out of every feature.',
	},
	{
		selector: '.dashboardCard-1',
		Icon: Target,
		title: 'Live Campaigns',
		body: 'The number of phishing simulations running right now across Email, SMS, WhatsApp, and Voice. Open it to see who has received, opened, or clicked each simulated attack.',
	},
	{
		selector: '.dashboardCard-2',
		Icon: UserCircle,
		title: 'Active Users',
		body: 'How many users are enrolled in your awareness programme. Jump straight to the directory to manage profiles and review individual security metrics.',
	},
	{
		selector: '.dashboardCard-3',
		Icon: Student,
		title: 'Training Modules',
		body: 'The number of courses available in your library. Browse them, build training campaigns, and assign courses by role or risk level.',
	},
	{
		selector: '.dashboardCard-4',
		Icon: EnvelopeSimpleOpen,
		title: 'Phishing Material',
		body: 'The count of phishing email templates ready for your simulations. Use them as-is or tailor them to mirror the real threats your organisation faces.',
	},
	{
		selector: '.dashboardCard-5',
		Icon: GaugeSimple,
		title: 'Organisation Risk Score',
		body: 'A single 0–100 measure of your security posture, drawn from simulation behaviour, training completion, and response times. Lower is riskier — watch it trend as your programme matures.',
	},
	{
		selector: '.dashboardCard-6',
		Icon: ChartLineUp,
		title: 'Campaign History',
		body: 'How your people have responded over time: links clicked (high risk), emails opened but not clicked (medium), and emails not opened. Use the trend to target follow-up training.',
	},
	{
		selector: '.dashboardCard-7',
		Icon: Broadcast,
		title: 'Running Campaign Details',
		body: 'Live status for each active campaign — targets, delivery, open and click rates, and reported emails — so you can spot who needs a nudge or extra training.',
	},
];

// Chapter grouping (guide §16): the sidebar/nav steps are one chapter, the
// dashboard-card steps another. Chapters are computed from whatever steps are
// actually visible so the navigator stays accurate per user.
const chapterOf = (step) =>
	step.selector.startsWith('.dashboardCard') ? 'Your dashboard' : 'Explore';

const computeChapters = (list) => {
	const chapters = [];
	list.forEach((s, i) => {
		const name = chapterOf(s);
		const last = chapters[chapters.length - 1];
		if (last && last.name === name) last.end = i;
		else chapters.push({ name, start: i, end: i });
	});
	return chapters;
};

// Build the @reactour step objects from a list of step definitions.
const buildSteps = (list) => {
	const chapters = computeChapters(list);
	return list.map((step) => ({
		selector: step.selector,
		content: ({ currentStep }) => (
			<RichStep
				Icon={step.Icon}
				title={step.title}
				body={step.body}
				currentStep={currentStep}
				totalSteps={list.length}
				chapters={chapters}
			/>
		),
	}));
};

export const steps = buildSteps(STEP_DATA);

// Return only the steps whose target element is actually on screen right now.
// Modules/cards are gated by role and licensed services, so this prevents the
// tour from anchoring a coachmark to an element that isn't rendered for this
// user. Call it right before opening the tour (see setSteps usage).
export const getVisibleSteps = () => {
	if (typeof document === 'undefined') return steps;
	const visible = STEP_DATA.filter((step) => {
		try {
			return !!document.querySelector(step.selector);
		} catch {
			return false;
		}
	});
	return buildSteps(visible);
};
